import { apiClient } from './client';
import type { ApiSuccess, PaginationMeta } from '../types';

export interface AmTransfer { id: number; action: string; fromAssigneeType: string | null; fromAssigneeId: number | null; fromLocation: string | null; toAssigneeType: string | null; toAssigneeId: number | null; toLocation: string | null; transferDate: string | null; reason: string | null; createdAt: string }
export interface AmAudit { id: number; auditDate: string | null; foundStatus: string | null; foundCondition: string | null; foundLocation: string | null; verified: boolean; remarks: string | null; createdAt: string }

export interface AmAsset {
  id: number;
  assetCode: string;
  name: string;
  category: string | null;
  serialNumber: string | null;
  status: 'in_stock' | 'allocated' | 'in_transit' | 'under_maintenance' | 'retired';
  condition: 'new' | 'good' | 'fair' | 'poor' | 'damaged';
  purchaseDate: string | null;
  purchaseCost: number | null;
  currentValue: number | null;
  depreciationMethod: 'none' | 'straight_line' | 'wdv';
  usefulLifeYears: number | null;
  depreciationRate: number | null;
  salvageValue: number | null;
  depreciatedValue: number | null;
  location: string | null;
  assignedToType: string | null;
  assignedToId: number | null;
  remarks: string | null;
  photos: string[];
  createdAt: string;
  transfers?: AmTransfer[];
  audits?: AmAudit[];
}

/* ---- Asset repair / BER assessment ---- */
export interface AmAssetRepair {
  id: number; repairCode: string; assetId: number; repairDescription: string | null;
  repairCost: number; assetValueAtAssessment: number; berThresholdPercent: number; repairRatio: number;
  isBer: boolean; recommendation: 'go_ahead' | 'ber'; status: 'pending' | 'approved' | 'rejected' | 'on_hold' | 're_estimate';
  decisionRemarks: string | null; decidedBy: number | null; decidedAt: string | null; createdAt: string;
  asset?: { id: number; assetCode: string; name: string; status: string } | null;
  suggestRetire?: boolean;
}
export type RepairDecision = 'approve' | 'reject' | 'hold' | 're_estimate';

/* ---- Asset transfer (with approval) ---- */
export type TransferToType = 'site' | 'warehouse' | 'employee' | 'vendor' | 'technician';
export type TransferAction = 'approve' | 'reject' | 'cancel';
export interface AmAssetTransfer {
  id: number; transferCode: string; assetId: number;
  fromType: string | null; fromId: number | null; fromLocation: string | null;
  toType: TransferToType; toRefId: number | null; toName: string | null;
  transferDate: string | null; reason: string | null; documentUrl: string | null;
  status: 'pending' | 'approved' | 'rejected' | 'cancelled';
  requestedBy: number | null; decidedBy: number | null; decisionRemarks: string | null; decidedAt: string | null; createdAt: string;
  assetCode?: string | null; assetName?: string | null;
  asset?: { id: number; assetCode: string; name: string; status: string } | null;
}

interface Paginated<T> { data: T[]; meta: PaginationMeta }

export const assetManagementApi = {
  async list(params: Record<string, unknown> = {}): Promise<Paginated<AmAsset>> {
    const { data } = await apiClient.get<ApiSuccess<AmAsset[]>>('/am-assets', { params });
    return { data: data.data, meta: data.meta as PaginationMeta };
  },
  get: async (id: number) => (await apiClient.get<ApiSuccess<AmAsset>>(`/am-assets/${id}`)).data.data,
  register: async (body: { name: string; category?: string; serialNumber?: string; condition?: string; purchaseDate?: string; purchaseCost?: number; depreciationMethod?: string; usefulLifeYears?: number; depreciationRate?: number; salvageValue?: number; location?: string; remarks?: string; stationId?: number; photos?: string[] }) =>
    (await apiClient.post<ApiSuccess<AmAsset>>('/am-assets', body)).data.data,
  update: async (id: number, body: Record<string, unknown>) => (await apiClient.put<ApiSuccess<AmAsset>>(`/am-assets/${id}`, body)).data.data,
  allocate: async (id: number, body: { assigneeType: string; assigneeId: number; location?: string; reason?: string }) =>
    (await apiClient.post<ApiSuccess<AmAsset>>(`/am-assets/${id}/allocate`, body)).data.data,
  transfer: async (id: number, body: { toAssigneeType?: string; toAssigneeId?: number; toLocation?: string; reason?: string }) =>
    (await apiClient.post<ApiSuccess<AmAsset>>(`/am-assets/${id}/transfer`, body)).data.data,
  audit: async (id: number, body: { auditDate?: string; foundStatus?: string; foundCondition?: string; foundLocation?: string; verified?: boolean; remarks?: string }) =>
    (await apiClient.post<ApiSuccess<AmAsset>>(`/am-assets/${id}/audit`, body)).data.data,
  retire: async (id: number, reason?: string) => (await apiClient.post<ApiSuccess<AmAsset>>(`/am-assets/${id}/retire`, { reason })).data.data,
};

export const assetRepairsApi = {
  async list(params: Record<string, unknown> = {}): Promise<Paginated<AmAssetRepair>> {
    const { data } = await apiClient.get<ApiSuccess<AmAssetRepair[]>>('/am-asset-repairs', { params });
    return { data: data.data, meta: data.meta as PaginationMeta };
  },
  get: async (id: number) => (await apiClient.get<ApiSuccess<AmAssetRepair>>(`/am-asset-repairs/${id}`)).data.data,
  assess: async (body: { assetId: number; repairDescription?: string; repairCost: number; berThresholdPercent?: number }) =>
    (await apiClient.post<ApiSuccess<AmAssetRepair>>('/am-asset-repairs', body)).data.data,
  decide: async (id: number, decision: RepairDecision, remarks?: string) =>
    (await apiClient.post<ApiSuccess<AmAssetRepair>>(`/am-asset-repairs/${id}/decision`, { decision, remarks })).data.data,
};

export const assetTransfersApi = {
  async list(params: Record<string, unknown> = {}): Promise<Paginated<AmAssetTransfer>> {
    const { data } = await apiClient.get<ApiSuccess<AmAssetTransfer[]>>('/am-asset-transfers', { params });
    return { data: data.data, meta: data.meta as PaginationMeta };
  },
  get: async (id: number) => (await apiClient.get<ApiSuccess<AmAssetTransfer>>(`/am-asset-transfers/${id}`)).data.data,
  request: async (body: { assetId: number; toType: TransferToType; toRefId?: number; toName?: string; transferDate?: string; reason?: string; documentUrl?: string }) =>
    (await apiClient.post<ApiSuccess<AmAssetTransfer>>('/am-asset-transfers', body)).data.data,
  decide: async (id: number, decision: TransferAction, remarks?: string) =>
    (await apiClient.post<ApiSuccess<AmAssetTransfer>>(`/am-asset-transfers/${id}/decision`, { decision, remarks })).data.data,
};
